import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { BrandField } from "../form/BrandField";
import { BrandButton } from "../ui/BrandButton";
import { BrandSelect } from "../form/BrandSelect";
import { brandFieldTitleClass, brandInputClass } from "../form/fieldStyles";

function ModalFrame({ title, eyebrow, description, onClose, children, ariaLabelledBy }) {
  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 px-4 py-4 backdrop-blur-md"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="max-h-[calc(100dvh-2rem)] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-[2rem] border border-white/20 bg-white/95 p-5 shadow-soft backdrop-blur sm:max-h-[calc(100vh-2rem)] sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
      >
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-secondary">
          {eyebrow}
        </p>
        <h2 id={ariaLabelledBy} className="mt-2 font-display text-3xl text-brand-primary">
          {title}
        </h2>
        {description ? (
          <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
        ) : null}

        <div className="mt-6">{children}</div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}

function TextField({ label, value, onChange, placeholder, type = "text", autoComplete }) {
  return (
    <label className="grid gap-2">
      <span className={brandFieldTitleClass}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={brandInputClass}
      />
    </label>
  );
}

function toRoleOptions(canEditRole) {
  if (!canEditRole) {
    return [];
  }

  return [
    { value: "user", label: "Usuario" },
    { value: "admin", label: "Admin" },
    { value: "superadmin", label: "Superadmin" },
  ];
}

export function UserEditModal({
  user,
  canEditRole = false,
  institutions = [],
  tnuaOptions = [],
  isSaving,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState({
    name: "",
    lastName: "",
    username: "",
    email: "",
    roleName: "user",
    institutionId: "",
    tnuaId: "",
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    setForm({
      name: user.name ?? "",
      lastName: user.lastName ?? "",
      username: user.username ?? "",
      email: user.email ?? "",
      roleName: user.role?.name ?? "user",
      institutionId: user.institution?.id ?? "",
      tnuaId: user.tnua?.id ?? "",
    });
  }, [user]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  return (
    <ModalFrame
      eyebrow="Edición"
      title="Editar usuario"
      description={`Actualizá los datos de ${user?.name ?? ""} ${user?.lastName ?? ""}.`}
      onClose={onClose}
      ariaLabelledBy="edit-user-title"
    >
      <form className="grid gap-4" onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Nombre"
            value={form.name}
            onChange={handleChange("name")}
            placeholder="Nombre"
          />
          <TextField
            label="Apellido"
            value={form.lastName}
            onChange={handleChange("lastName")}
            placeholder="Apellido"
          />
          <TextField
            label="Usuario"
            value={form.username}
            onChange={handleChange("username")}
            placeholder="usuario"
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={handleChange("email")}
            placeholder="usuario@mail.com"
            type="email"
            autoComplete="email"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <BrandSelect
            label="Tnua"
            name="tnuaId"
            value={form.tnuaId}
            onChange={handleChange("tnuaId")}
            options={[
              { value: "", label: "Sin tnua" },
              ...tnuaOptions.map((option) => ({
                value: option.id,
                label: option.name,
              })),
            ]}
            placeholder="Sin tnua"
          />
          <BrandSelect
            label="Institución Educativa"
            name="institutionId"
            value={form.institutionId}
            onChange={handleChange("institutionId")}
            options={[
              { value: "", label: "Sin institución" },
              ...institutions.map((option) => ({
                value: option.id,
                label: option.name,
              })),
            ]}
            placeholder="Sin institución"
          />
        </div>

        {canEditRole ? (
          <BrandSelect
            label="Rol"
            name="roleName"
            value={form.roleName}
            onChange={handleChange("roleName")}
            options={toRoleOptions(true)}
            placeholder="Usuario"
          />
        ) : null}

        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <BrandButton
            type="button"
            onClick={onClose}
            variant="outline"
            radius="2xl"
            size="lg"
            motion="subtle"
          >
            Cancelar
          </BrandButton>
          <BrandButton type="submit" disabled={isSaving} radius="2xl" size="lg">
            {isSaving ? "Guardando..." : "Guardar cambios"}
          </BrandButton>
        </div>
      </form>
    </ModalFrame>
  );
}

export function UserStatusModal({ user, action, isSaving, onClose, onConfirm }) {
  const isDisable = action === "disable";

  return (
    <ModalFrame
      eyebrow="Estado"
      title={isDisable ? "Deshabilitar usuario" : "Habilitar usuario"}
      description={
        isDisable
          ? `El usuario ${user?.username ? `@${user.username}` : ""} no podrá iniciar sesión mientras esté deshabilitado.`
          : `El usuario ${user?.username ? `@${user.username}` : ""} volverá a poder iniciar sesión.`
      }
      onClose={onClose}
      ariaLabelledBy="user-status-title"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <BrandButton
          type="button"
          onClick={onClose}
          variant="outline"
          radius="2xl"
          size="lg"
          motion="subtle"
        >
          Cancelar
        </BrandButton>
        <BrandButton
          type="button"
          onClick={onConfirm}
          disabled={isSaving}
          variant={isDisable ? "secondary" : "primary"}
          radius="2xl"
          size="lg"
        >
          {isSaving ? "Procesando..." : isDisable ? "Deshabilitar" : "Habilitar"}
        </BrandButton>
      </div>
    </ModalFrame>
  );
}

export function CreateAdminModal({ isSaving, onClose, onSubmit }) {
  const [form, setForm] = useState({
    name: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    passwordConfirm: "",
  });

  const handleChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  return (
    <ModalFrame
      eyebrow="Superadmin"
      title="Crear admin"
      description="Creá un nuevo usuario con rol de admin desde este formulario."
      onClose={onClose}
      ariaLabelledBy="create-admin-title"
    >
      <form className="grid gap-4" onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <BrandField
            label="Nombre"
            value={form.name}
            onChange={handleChange("name")}
            showLabel={false}
            placeholder="Nombre"
          />
          <BrandField
            label="Apellido"
            value={form.lastName}
            onChange={handleChange("lastName")}
            showLabel={false}
            placeholder="Apellido"
          />
          <BrandField
            label="Usuario"
            value={form.username}
            onChange={handleChange("username")}
            showLabel={false}
            placeholder="Usuario"
          />
          <BrandField
            label="Email"
            value={form.email}
            onChange={handleChange("email")}
            type="email"
            autoComplete="email"
            showLabel={false}
            placeholder="usuario@mail.com"
          />
          <BrandField
            label="Contraseña"
            value={form.password}
            onChange={handleChange("password")}
            type="password"
            autoComplete="new-password"
            showLabel={false}
            placeholder="Contraseña"
          />
          <BrandField
            label="Repetir contraseña"
            value={form.passwordConfirm}
            onChange={handleChange("passwordConfirm")}
            type="password"
            autoComplete="new-password"
            showLabel={false}
            placeholder="Repetir contraseña"
          />
        </div>

        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <BrandButton
            type="button"
            onClick={onClose}
            variant="outline"
            radius="2xl"
            size="lg"
            motion="subtle"
          >
            Cancelar
          </BrandButton>
          <BrandButton type="submit" disabled={isSaving} radius="2xl" size="lg">
            {isSaving ? "Creando..." : "Crear admin"}
          </BrandButton>
        </div>
      </form>
    </ModalFrame>
  );
}
